"use client";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { useDebounce } from "@/hooks/useDebounce";
import {
  DEFAULT_DEBOUNCE_DELAY,
  TAILWIND_SHAKE_ANIMATION_DELAY,
} from "@/lib/constants";
import { useOrganizationStore } from "@/stores/organization";
import { CirclePlus, LoaderCircle, RefreshCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { columns } from "./columns";
import Image from "next/image";
import ApiKeysCreateDialog from "@/components/api-keys-create";
import { MsgQuestion } from "@/components/msg-question";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTheme } from "@/providers/theme";
import { useRouter } from "next/navigation";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function SettingsApiKeys() {
  useDocumentTitle("API Keys");
  const { theme } = useTheme();
  const { organization, subscriptions, apiKeys, isLoading, getApiKeys } =
    useOrganizationStore();
  const isMobile = useIsMobile();
  const isLargeScreen = useMediaQuery("(max-width: 920px)");
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRefreshBlocked, setIsRefreshBlocked] = useState(false);
  const isInitialLoad = useRef(true);
  const router = useRouter();

  const handleApiKeysCreationRestriction = (e: React.MouseEvent) => {
    if (apiKeys.length > 0) {
      // @TODO: Remove whenever we allow multiple API keys for an organization
      e.preventDefault();
      toast.warning("Uh, oh! You already have an API key", {
        description:
          "For the moment, you only need one API key per organization. This may change in the future.",
      });
    } else {
      setIsCreateDialogOpen(true);
    }
  };

  const refreshApiKeys = async () => {
    if (!organization) return;

    try {
      setIsRefreshing(true);
      await getApiKeys(organization.id, true);
      setIsRefreshing(false);
    } catch (error: any) {
      console.warn(error.message);
    }
  };

  const debounceRefresh = useDebounce(
    refreshApiKeys,
    DEFAULT_DEBOUNCE_DELAY,
    () => {
      setIsRefreshBlocked(true);
      setTimeout(
        () => setIsRefreshBlocked(false),
        TAILWIND_SHAKE_ANIMATION_DELAY,
      );
    },
  );

  useEffect(() => {
    if (!isLoading && isInitialLoad.current) {
      isInitialLoad.current = false;

      const hasActiveSubscription = subscriptions.some(
        (s) =>
          s.productId === process.env.NEXT_PUBLIC_STRIPE_PIPELINES_PRODUCT_ID,
      );

      if (!hasActiveSubscription && apiKeys.length > 0) {
        setHasActiveSubscription(false);
        toast.warning("Missing Subscription", {
          description:
            "There is no active PowerOn Pipelines subscription for this organization which may cause API keys to not work.",
        });
      } else {
        setHasActiveSubscription(hasActiveSubscription);
      }
    }
  }, [isLoading, subscriptions, apiKeys]);

  return (
    <>
      <div className="lg:col-span-9">
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center lg:gap-2">
          <div className="flex flex-grow items-center justify-between">
            <h2 className="mt-1.5 flex flex-row gap-2 text-xl font-semibold leading-6 text-gray-1100 dark:text-gray-100 lg:mt-0">
              API&nbsp;Keys
              <span className="gap-2 text-muted">({apiKeys.length})</span>
            </h2>
            <Button
              variant="ghost"
              onClick={debounceRefresh}
              className={`size-8 self-end ${isRefreshBlocked ? "animate-shake" : ""}`}
            >
              <RefreshCcw />
            </Button>
          </div>

          <div className="mt-0 flex flex-grow flex-col items-end gap-2 sm:flex-row sm:items-center lg:flex-grow-0">
            <Input
              type="search"
              placeholder="Find an API key"
              className="mt-0 w-full lg:w-[400px]"
              id="user-search"
            />
            <Button
              className="w-fit"
              disabled={!hasActiveSubscription}
              onClick={handleApiKeysCreationRestriction}
            >
              <CirclePlus />
              Create new secret key
            </Button>
          </div>
        </div>
        <div className="lb-scrollbar-overlay -mx-3.5 overflow-x-auto py-6 sm:-mx-5">
          {(isInitialLoad.current && isLoading) ||
          (isRefreshing && isLoading) ? (
            <LoaderCircle className="mx-auto mt-16 flex size-8 animate-spin text-brand-primary" />
          ) : (
            <DataTable
              columns={columns(hasActiveSubscription).filter((c: any) =>
                isMobile || isLargeScreen
                  ? ["apiKey", "product"].includes(c.accessorKey) ||
                    ["actions"].includes(c.id)
                  : true,
              )}
              data={apiKeys}
              searchField={["createdBy", "product"]}
              searchInputId="user-search"
              noResults={
                <div className="flex flex-1 items-center justify-center py-[6rem]">
                  <div className="flex max-w-md flex-col items-center px-4 text-center">
                    {hasActiveSubscription ? (
                      <>
                        <Image
                          src={`/emojis/${theme === "dark" ? "key" : "old-key"}.svg`}
                          alt=""
                          width={36}
                          height={36}
                        />
                        <h2 className="py-3 text-lg font-medium leading-6 text-gray-1100 dark:text-gray-100 lg:mt-0">
                          Create an API key to use PowerOn Pipelines
                        </h2>
                        <Button
                          className="w-fit"
                          disabled={!hasActiveSubscription}
                          onClick={handleApiKeysCreationRestriction}
                        >
                          <CirclePlus />
                          Create new secret key
                        </Button>
                      </>
                    ) : (
                      <>
                        <Image
                          src="/emojis/palm-tree.svg"
                          alt=""
                          width={36}
                          height={36}
                        />
                        <h2 className="py-3 text-lg font-medium leading-6 text-gray-1100 dark:text-gray-100 lg:mt-0">
                          Subscribe to PowerOn Pipelines to use API keys
                        </h2>
                        <Button
                          className="w-fit"
                          onClick={() => {
                            router.push(
                              "/settings/subscriptions?type=poweron-pipelines",
                            );
                          }}
                        >
                          View Subscriptions
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              }
            />
          )}
        </div>
        <MsgQuestion name="API keys" />
      </div>
      <ApiKeysCreateDialog
        controlledOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />
    </>
  );
}
